import { VirtualScroller, type ScrollBlock } from '../VirtualScroller/VirtualScroller';
import './VirtualGridTable.scss';

export type VirtualGridTableColumn<T> = {
    key: keyof T & string;
    header: string | HTMLElement | SVGElement;
    /** Ejemplo: '1fr', '100px' */
    width?: string;
    minWidth?: string;
    renderCell?: (item: T, index: number) => string | HTMLElement | SVGElement;
};

export type VirtualGridTableOptions<T> = {
    columns: VirtualGridTableColumn<T>[];
    estimatedRowHeight: number;
    getKey: (item: T, index: number) => number | string
    beforeRender?: (items: T[]) => void;
    items?: T[];
    /** Ejemplo: '400px' */
    height?: string;
    buffer?: number;
} & ({
    useRealRows: true
    onRenderRow?: (row: HTMLDivElement, item: T, index: number) => void;
} | { useRealRows: false });

export class VirtualGridTable<T> {
    private readonly container: HTMLDivElement;
    private readonly header: HTMLElement;
    private readonly scrollContainer: HTMLElement;
    private readonly body: HTMLElement;
    private readonly spacer: HTMLElement;
    private readonly options: VirtualGridTableOptions<T>;
    private readonly scroller: VirtualScroller<T>;
    private items: T[] = [];

    constructor(options: VirtualGridTableOptions<T>) {
        this.options = options;

        // Crear template
        const template = document.createElement("template");
        template.innerHTML = `
        <div class="virtual-grid-table-container">
            <div class="grid-table-header"></div>

            <div class="scroll-container">
                <div class="spacer"></div>
                <div class="grid-table-body"></div>
            </div>
        </div>`;

        // Contenedor principal
        this.container = template.content.querySelector<HTMLDivElement>(".virtual-grid-table-container")!;
        if (options.height) this.container.style.height = options.height;

        // Crear header
        this.header = template.content.querySelector<HTMLDivElement>(".grid-table-header")!;

        for (const col of options.columns) {
            const cell = document.createElement('div');
            cell.className = 'grid-table-header-cell';
            if (typeof col.header == "string") {
                cell.textContent = col.header;
            } else if (col.header) {
                cell.append(col.header);
            }
            this.header.appendChild(cell);
        }

        // Contenedor de VirtualScroller, spacer y body
        this.scrollContainer = template.content.querySelector<HTMLDivElement>(".scroll-container")!

        this.spacer = template.content.querySelector<HTMLDivElement>(".spacer")!

        this.body = template.content.querySelector<HTMLDivElement>(".grid-table-body")!;

        this.container.style.setProperty("--grid-template-columns", this.getGridTemplateColumns());

        if (this.options.useRealRows) {
            this.container.classList.add("use-real-rows");
        }

        this.scroller = new VirtualScroller<T>({
            estimatedRowHeight: options.estimatedRowHeight,
            buffer: options.buffer ?? 0,
            getKey: options.getKey,
        });

        this.scrollContainer.addEventListener('mousedown', () => { this.cancelAutoScroll(); });
        this.scrollContainer.addEventListener('touchstart', () => { this.cancelAutoScroll(); });
        this.scrollContainer.addEventListener('wheel', () => { this.cancelAutoScroll(); });

        this.scrollContainer.addEventListener('scroll', () => { this.render(); });

        this.container.addEventListener("resize", () => { this.render(); });

        if (options.items?.length)
            this.setItems(options.items);
    }

    // **********************************************************************
    // PUBLIC METHODS
    // **********************************************************************

    public get element(): HTMLDivElement {
        return this.container;
    }

    public setItems(items: T[]): void {
        this.items = items;
        this.scroller.setItems(this.items);
        this.cancelAutoScroll();
        this.render(true);
        // setTimeout(() => { this.render(true); }, 100);
    }

    public refresh(): void {
        this.cancelAutoScroll();
        this.render(true);
    }

    public scrollToItemID(id: number | string, options?: ScrollOptions): void {
        this.scrollToItem(
            () => this.scroller.getOffsetRow(id),
            options || null,
            true
        );
    }

    public scrollToItemIndex(index: number, options?: ScrollOptions): void {
        this.scrollToItem(
            () => this.scroller.getOffsetRowByIndex(index),
            options || null,
            true
        );
    }

    // **********************************************************************
    // PRIVATE METHODS
    // **********************************************************************

    private scrollToItem(
        getOffset: () => number,
        options: ScrollOptions | null,
        isFirstCall: boolean,
    ): void {
        const offset = getOffset();

        if (
            offset === -1 // item no encontrado
            || (!isFirstCall && this.scrollContainer.onscrollend == null) // auto-scroll cancelado
        ) {
            return;
        }

        this.scrollContainer.scrollTo({
            top: offset,
            behavior: options?.behavior,
        });

        this.scrollContainer.onscrollend = () => { // Se valida si el scroll se ubicó justamente donde el item
            if (this.scrollContainer.onscrollend == null) return;

            const offsetFinal = getOffset();

            if (offsetFinal !== -1 && offset != offsetFinal) {
                this.scrollToItem(getOffset, options, false);
            } else {
                this.cancelAutoScroll();
            }
        }
    }

    private cancelAutoScroll() {
        if (this.scrollContainer.onscrollend) {
            this.scrollContainer.onscrollend = null;
        }
    }

    private getGridTemplateColumns(): string {
        return this.options.columns.map(col => !col.minWidth
            ? (col.width ?? '1fr')
            : `minmax(${col.minWidth}, ${col.width ?? '1fr'})`
        ).join(' ');
    }

    private render(force = false): void {
        const scrollTop = this.scrollContainer.scrollTop;
        const viewportHeight = this.scrollContainer.clientHeight;

        // Actualiza el espacio total
        this.spacer.style.height = this.scroller.getTotalHeight() + 'px';

        // Calcula el contexto de renderizado virtual
        const context = this.scroller.getRenderContextWithRealScroll(
            scrollTop,
            viewportHeight,
            this.spacer.scrollHeight
        );

        // Valida si se puede continuar con el renderizado
        const lastchildsStr: string | undefined = this.body.dataset["__lastchilds"]
        const newChilds = context.items.map(d => d.key + "");
        const newChildsStr: string = newChilds.join(",");
        if (!force && lastchildsStr && lastchildsStr == newChildsStr) {
            return;
        }
        this.body.dataset["__lastchilds"] = newChildsStr;

        if (this.options.beforeRender) {
            this.options.beforeRender(context.items.map(d => d.data));
        }

        const useRealRows = !!this.options.useRealRows;

        // Limpiar el body o remueve las filas que no necesitan renderizarse
        if (force) {
            this.body.innerHTML = '';
        } else {
            const lastchilds = lastchildsStr?.split(",");
            if (lastchilds) {
                lastchilds.forEach(key => {
                    if (newChilds.includes(key)) return;
                    this.body.querySelectorAll(`*[key-${key}]`).forEach(elem => {
                        // console.warn("removiendo row", key, elem.className);
                        elem.remove();
                    })
                })
            }
        }

        this.body.style.top = context.offsetTop + "px"; // ajustar el offset visual
        // this.body.style.transform = `translateY(${context.offsetTop}px)`;

        // Renderiza solo las filas visibles
        let viewportGridRow = 0;
        for (const item of context.items) {
            viewportGridRow++

            if (!force) {
                // Validar si la fila ya existe
                const existsRow = this.body.querySelectorAll<HTMLElement>(`*[key-${item.key}]`); // row | cells

                // if (existsRow.length) console.warn(item.key, ...existsRow)
                if (existsRow.length) {
                    existsRow.forEach(el => {
                        el.style.gridRow = viewportGridRow + "";
                    })
                    continue;
                }
            }

            // cells container
            let cellsContainer: HTMLElement | HTMLDivElement; // body | row
            if (useRealRows) {
                cellsContainer = document.createElement("div");
                cellsContainer.setAttribute(`row-${item.index}`, "");
                cellsContainer.setAttribute(`key-${item.key}`, "");
                cellsContainer.className = "grid-table-row";
                cellsContainer.style.gridColumn = "1"
                cellsContainer.style.gridRow = viewportGridRow + "";
                this.body.append(cellsContainer);
                if (this.options.onRenderRow) {
                    this.options.onRenderRow(cellsContainer as HTMLDivElement, item.data, item.index);
                }
            } else {
                cellsContainer = this.body
            }

            // Build cells
            for (let colIdx = 0; colIdx < this.options.columns.length; colIdx++) {
                const col = this.options.columns[colIdx];
                const cell = document.createElement('div');

                // cell config
                cell.className = 'grid-table-cell';
                cell.setAttribute(`row-${item.index}`, "");
                cell.setAttribute(`cell-${colIdx}`, "");
                if (!useRealRows) {
                    cell.setAttribute(`key-${item.key}`, "");
                }
                cell.style.gridColumn = `${colIdx + 1}`;
                cell.style.gridRow = `${useRealRows ? 1 : viewportGridRow}`; // `auto`;

                // cell content
                let content: string | HTMLElement | SVGElement | undefined = (item.data as any)[col.key];
                if (col.renderCell) {
                    content = col.renderCell(item.data, item.index);
                }
                if (typeof content === 'string') {
                    cell.textContent = content;
                } else if (content) {
                    if (content instanceof HTMLElement || content instanceof SVGElement) {
                        cell.appendChild(content);
                    } else {
                        cell.textContent = content + "";
                    }
                }
                cellsContainer.appendChild(cell);
            }

            // Medir altura real y actualizar si es diferente a la estimada
            let elementDimsRef: HTMLElement
            if (this.options.useRealRows) {
                elementDimsRef = cellsContainer // Current Row
            } else {
                const firstCellIdx = this.body.children.length - this.options.columns.length;
                const firstCell = this.body.children[firstCellIdx] as HTMLElement;
                elementDimsRef = firstCell;
            }
            if (elementDimsRef) {
                const h = elementDimsRef.clientHeight;
                if (
                    this.options.estimatedRowHeight &&
                    h !== this.options.estimatedRowHeight &&
                    h !== this.scroller.getRowHeight(item.key)
                ) {
                    this.scroller.setRowHeight(item.key, h);
                }
            }
        }
    }
}

interface ScrollOptions {
    behavior?: "smooth" | "instant";
    block?: ScrollBlock;
}